import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google: the callback page exchanges the session first, so skip the /me check here.
    if (window.location.hash?.includes("session_id=")) return;
    load();
  }, [load]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) return { ok: false, mfa: data };
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.mfa_required) return { ok: false, mfa: data };
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed; clearing the session locally", err?.message || err);
    }
    setUser(false);
  };

  const switchMode = async (mode) => {
    const { data } = await api.post("/auth/mode", { mode });
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, reload: load, setSession: setUser, switchMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
